/**
 * Lens Sort Menu Component
 * Dropdown menu for sorting work queue issues
 */

import { memo, useCallback } from 'react'
import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItemRadio,
  MenuGroup,
  MenuGroupHeader,
  Button,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import type { MenuProps } from '@fluentui/react-components'
import { ArrowSortRegular } from '@fluentui/react-icons'
import type { WorkQueueSortBy, WorkQueueSortDirection } from '../types/workQueueFilters'
import { useWorkQueueFilter } from '../context'

const useStyles = makeStyles({
  trigger: {
    color: tokens.colorNeutralForegroundOnBrand,
    ':hover': {
      color: tokens.colorNeutralForegroundOnBrand,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
})

interface SortOption {
  id: string
  label: string
  sortBy: WorkQueueSortBy
  sortDirection: WorkQueueSortDirection
}

const SORT_OPTIONS: SortOption[] = [
  { id: 'newest', label: 'Newest First', sortBy: 'createdAt', sortDirection: 'desc' },
  { id: 'oldest', label: 'Oldest First', sortBy: 'createdAt', sortDirection: 'asc' },
  { id: 'severity-high', label: 'Severity (High-Low)', sortBy: 'severity', sortDirection: 'asc' },
  { id: 'severity-low', label: 'Severity (Low-High)', sortBy: 'severity', sortDirection: 'desc' },
]

export const LensSortMenu = memo(function LensSortMenu() {
  const styles = useStyles()
  const { sortBy, sortDirection, setSort } = useWorkQueueFilter()

  const currentOptionId = SORT_OPTIONS.find(
    (opt) => opt.sortBy === sortBy && opt.sortDirection === sortDirection
  )?.id ?? 'newest'

  const handleOptionSelect = useCallback(
    (optionId: string) => {
      const option = SORT_OPTIONS.find((opt) => opt.id === optionId)
      if (option) {
        setSort(option.sortBy, option.sortDirection)
      }
    },
    [setSort]
  )

  const handleCheckedValueChange = useCallback<NonNullable<MenuProps['onCheckedValueChange']>>(
    (_event, data) => {
      const selectedId = data.checkedItems[0]
      if (selectedId) {
        handleOptionSelect(selectedId)
      }
    },
    [handleOptionSelect]
  )

  return (
    <Menu checkedValues={{ sort: [currentOptionId] }} onCheckedValueChange={handleCheckedValueChange}>
      <MenuTrigger disableButtonEnhancement>
        <Button
          appearance="transparent"
          icon={<ArrowSortRegular />}
          className={styles.trigger}
        >
          Sort
        </Button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuGroup>
            <MenuGroupHeader>Sort by</MenuGroupHeader>
            {SORT_OPTIONS.map((option) => (
              <MenuItemRadio
                key={option.id}
                name="sort"
                value={option.id}
              >
                {option.label}
              </MenuItemRadio>
            ))}
          </MenuGroup>
        </MenuList>
      </MenuPopover>
    </Menu>
  )
})
